import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

/**
 * The "Checkout at <retailer>" CTA plus its leave-site confirmation modal.
 *
 * Extends {@link DialogComponent} so it inherits `showDialog` / `closeDialog`. When the CTA is
 * clicked it checks the current cart:
 *   - empty cart      -> does nothing special; the underlying `<a target="_blank">` opens the
 *                        retailer in a new tab natively (there's no cart to lose, and letting the
 *                        browser handle the gesture avoids popup blocking).
 *   - cart has items  -> prevents navigation and opens a modal warning that the cart won't
 *                        transfer. Confirm opens the retailer in a new tab; Cancel dismisses.
 *
 * The cart count is seeded from Liquid (`data-cart-count`), confirmed with a live `/cart.js`
 * read on connect, and kept current via `cart:update` events — so the click-time decision
 * always reflects the live cart without an async fetch in the click handler.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The confirmation dialog.
 * @property {HTMLAnchorElement} trigger - The "Checkout at <retailer>" link.
 *
 * @extends DialogComponent
 */
export class RetailerCheckoutComponent extends DialogComponent {
  requiredRefs = ['dialog', 'trigger'];

  /** @type {number} */
  #cartItemCount = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#cartItemCount = Number(this.dataset.cartCount) || 0;
    this.#refreshCartCount();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /**
   * Keeps the cached cart count in sync when the cart changes elsewhere on the page.
   * @param {CustomEvent} event
   */
  #handleCartUpdate = (event) => {
    const count = event.detail?.resource?.item_count;
    if (typeof count === 'number') {
      this.#cartItemCount = count;
    } else {
      this.#refreshCartCount();
    }
  };

  /** Reads the live cart so the click-time decision isn't based on a stale render. */
  async #refreshCartCount() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return;
      const cart = await response.json();
      this.#cartItemCount = cart.item_count ?? this.#cartItemCount;
    } catch {
      // Network failure: keep the last known count.
    }
  }

  /**
   * Handles a click on the "Checkout at <retailer>" link.
   * @param {MouseEvent} event
   */
  handleClick(event) {
    // Empty cart: nothing to preserve — let the link open the retailer in a new tab natively.
    if (this.#cartItemCount < 1) return;

    // Cart has items: warn before leaving instead of navigating away.
    event.preventDefault();
    this.showDialog();
  }

  /** Confirms leaving: opens the retailer in a new tab (preserving this session) and closes. */
  confirmCheckout() {
    window.open(this.refs.trigger.href, '_blank', 'noopener');
    this.closeDialog();
  }
}

if (!customElements.get('retailer-checkout-component')) {
  customElements.define('retailer-checkout-component', RetailerCheckoutComponent);
}
